/**
 * Prepares an image for the support chat BEFORE it leaves the computer.
 * Ported from the design prototype (same behaviour and limits).
 *
 * Two reasons, and the second weighs more:
 *  - Size: a phone photo has 4000 x 3000 pixels and 3 to 6 MB. For "this is what
 *    my screen looks like" 2000 pixels on the long edge are enough.
 *  - Metadata: phone photos carry EXIF (GPS position, device, time). A photo of
 *    the practice screen taken at the front desk would give away exactly that
 *    place. Redrawn on a canvas, only the picture itself remains.
 *
 * Screenshots stay as they are when they are small enough: PNG carries no GPS,
 * and JPEG blurs text, while on a screenshot the text is exactly what the team
 * has to read. A format the browser cannot open (HEIC in Chrome) is refused with
 * a message the practice understands instead of being uploaded broken.
 */
const MAX_EDGE = 2000;
/**
 * PNG may stay larger. A screenshot is text: scaled to 2000 (from 2560, an odd
 * factor) it goes soft, re-encoded as JPEG it frays. Flat interfaces compress
 * well as PNG; a dashboard at 2560 usually stays under 1 MB.
 */
const MAX_EDGE_PNG = 2880;
const JPEG_QUALITY = 0.86;
/**
 * Smaller than 16 pixels is no photo. Files copied in the Finder and pasted give
 * the browser only a tiny preview, not the files, and the team got a dot it could
 * not enlarge. The limit holds wherever such an image comes from.
 */
const MIN_EDGE = 16;
const DEFAULT_MAX_BYTES = 6 * 1024 * 1024;
const NOT_PREPARED = 'Das Bild konnte nicht vorbereitet werden.';

/** Every failure leaves as an Error whose message can be shown to the practice. */
const refuse = (message) => Object.assign(new Error(message), { forPractice: true });

const open = async (file) => {
  // imageOrientation: a phone photo would otherwise lie on its side; the rotation
  // lives in EXIF, which redrawing drops.
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file, { imageOrientation: 'from-image' }); } catch { /* try the <img> way below */ }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable')); };
    img.src = url;
  });
};

const toBlob = (canvas, type, quality) => new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const prepare = async (file, maxBytes) => {
  const type = String(file.type || '').toLowerCase();
  if (type === 'image/svg+xml') throw refuse('SVG-Dateien werden aus Sicherheitsgründen nicht angenommen.');
  if (type === 'image/gif') {
    // Redrawing would freeze an animation, so a GIF goes as it is, but limited.
    if (file.size > maxBytes) throw refuse('Das GIF ist zu groß.');
    return { blob: file, width: null, height: null };
  }
  let image;
  try { image = await open(file); } catch {
    throw refuse('Dieses Bildformat kann Ihr Browser nicht öffnen. Bitte als JPEG oder PNG speichern.');
  }
  try {
    const w0 = image.width;
    const h0 = image.height;
    if (Math.min(w0, h0) < MIN_EDGE) {
      throw refuse(`Das Bild ist nur ${w0} × ${h0} Pixel groß und wurde nicht angehängt. Bilder bitte einfügen oder ins Fenster ziehen.`);
    }
    const isPng = type === 'image/png';
    const factor = Math.min(1, (isPng ? MAX_EDGE_PNG : MAX_EDGE) / Math.max(w0, h0));
    const w = Math.round(w0 * factor);
    const h = Math.round(h0 * factor);

    // Unchanged when it fits anyway: the sharpest image is the one nobody touched.
    if (isPng && factor === 1 && file.size <= maxBytes) return { blob: file, width: w0, height: h0 };

    const draw = (width, height, flat) => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      // White underneath only for JPEG: a transparent PNG would turn black in it.
      if (flat) { ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, width, height); }
      ctx.drawImage(image, 0, 0, width, height);
      return canvas;
    };

    if (isPng) {
      // PNG stays PNG. Smaller if it must be, but never via JPEG, or the text frays.
      for (const part of [1, 0.75, 0.5]) {
        const pw = Math.round(w * part);
        const ph = Math.round(h * part);
        if (Math.min(pw, ph) < MIN_EDGE) break;
        const blob = await toBlob(draw(pw, ph, false), 'image/png');
        if (blob && blob.size <= maxBytes) return { blob, width: pw, height: ph };
      }
    }

    const blob = await toBlob(draw(w, h, true), 'image/jpeg', JPEG_QUALITY);
    if (!blob) throw refuse(NOT_PREPARED);
    if (blob.size > maxBytes) throw refuse('Das Bild ist auch verkleinert noch zu groß.');
    return { blob, width: w, height: h };
  } finally {
    if (image.close) image.close(); // the decoded bitmap, freed on every way out (a failed canvas too)
  }
};

/**
 * @returns {Promise<{ blob: Blob, width: number|null, height: number|null }>}
 * @throws {Error} with a message that can be shown to the practice
 */
export async function prepareImage(file, { maxBytes = DEFAULT_MAX_BYTES } = {}) {
  try {
    return await prepare(file, maxBytes);
  } catch (err) {
    // A canvas that fails (no 2d context, out of memory) must not show a technical message.
    throw err && err.forPractice ? err : refuse(NOT_PREPARED);
  }
}
